import React, { useEffect, useMemo, useState } from "react";

import { css } from "@compiled/react";
import { ComponentInstance } from "@uniformdev/canvas";
import {
    ComponentProps,
    UniformSlot,
    UniformSlotWrapperComponentProps,
    useUniformCurrentComposition
} from "@uniformdev/canvas-react";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";

import { wacToken } from "@/libraries/design/foundations";
import { useExperiment } from "@/utils/experiment";
import { checkFeatureFlagGate, featureFlags } from "@/utils/feature-flags";
import { isProduction } from "@/utils/urls";

import { useAnalyticsClient } from "../../../../analytics/wac-analytics";
import { useWAEContext } from "../../../utils/wae-context-provider";

import {
    PersonalizationOverrideControls,
    PersonalizationUserSegmentValues
} from "./personalization-controls";
import {
    SegmentStatus,
    UserSegmentTypes,
    useUserSegments
} from "./use-user-segments";

export interface UniformPersonalizationContainerProps extends ComponentProps {
    containerName: string;
    experimentId?: string;
}

interface PersonalizedSectionProps
    extends UniformPersonalizationContainerProps {
    overrides?: PersonalizationUserSegmentValues;
    experimentId?: string;
    setRenderMessage: React.Dispatch<React.SetStateAction<string>>;
    requiredSegmentTypes: UserSegmentTypes[];
}
interface ExperimentallyPersonalizedSectionProps
    extends UniformPersonalizationContainerProps {
    overrides?: PersonalizationUserSegmentValues;
    experimentId: string;
    setRenderMessage: React.Dispatch<React.SetStateAction<string>>;
    variantComponent?: React.ReactNode;
    setVariantStatus: React.Dispatch<
        React.SetStateAction<VariantsStatus | null>
    >;
}

enum VariantsStatus {
    NO_VARIANT_MATCH = "no_variant_match",
    VARIANTS_AVAILABLE = "variants_available",
    FETCHING_SEGMENTS = "fetching_segments",
    SEGMENT_FETCH_ERROR = "segment_fetch_error",
    MISSING_USER_SEGMENTS = "missing_user_segments",
    PERSONALIZATION_DISABLED = "personalization_disabled",
    NOT_ENROLLED_IN_EXPERIMENT = "not_enrolled_in_experiment"
}

const renderMessages: Record<VariantsStatus, string> = {
    [VariantsStatus.NO_VARIANT_MATCH]: "No variants match. Rendering Default",
    [VariantsStatus.MISSING_USER_SEGMENTS]:
        "No User Segments acquired. Rendering Default",
    [VariantsStatus.PERSONALIZATION_DISABLED]:
        "Personalization is disabled. Rendering Default",
    [VariantsStatus.SEGMENT_FETCH_ERROR]:
        "Error fetching User Segments. Rendering Default",
    [VariantsStatus.NOT_ENROLLED_IN_EXPERIMENT]:
        "User not enrolled in experiment. Rendering Default",
    [VariantsStatus.FETCHING_SEGMENTS]:
        "Fetching User Segments. Temporarily rendering Default",
    [VariantsStatus.VARIANTS_AVAILABLE]: ""
};

const VariantSelector = (props: UniformSlotWrapperComponentProps) => {
    return (
        // this should only be one item, but the SlotWrapper expects an array
        <React.Fragment>
            {props.items.map((item, index) => (
                <React.Fragment key={`variant-${index}`}>{item}</React.Fragment>
            ))}
        </React.Fragment>
    );
};

// Helper function to render default slot
const renderDefaultSlot = (
    variantStatus: VariantsStatus,
    setRenderMessage: React.Dispatch<React.SetStateAction<string>>
) => {
    setRenderMessage(renderMessages[variantStatus]);
    return <UniformSlot name="default" wrapperComponent={VariantSelector} />;
};

/**
 * All supported segment operators.
 *
 * Multi-select operators (used with discrete values like owned-products, job-function):
 *   "is"      — user value IS one of the list
 *   "is_not"  — user value is NOT any of the list
 *
 * Free-text operators (used with string values like cluster-tag, geolocation):
 *   "includes"          — user value contains the string
 *   "not_includes"      — user value does not contain the string
 *   "matches"           — user value exactly matches the string
 *   "not_matches"       — user value does not exactly match the string
 *   "matches_regex"     — user value matches the regex
 *   "not_matches_regex" — user value does not match the regex
 */
export type SegmentOperator =
    | "is"
    | "is_not"
    | "includes"
    | "not_includes"
    | "matches"
    | "not_matches"
    | "matches_regex"
    | "not_matches_regex";

/**
 * A single segment rule. The operator field is optional for backward-compatibility
 * with legacy data — absent operator defaults to "is".
 */
export type SegmentRule = {
    segmentType: UserSegmentTypes;
    value: string[];
    operator?: SegmentOperator;
};

/**
 * A group of segment rules combined with a matchMode.
 *
 * matchMode:
 *   "any" — OR logic: at least one rule in the group must pass
 *   "all" — AND logic: every rule in the group must pass
 *
 * All groups in a variant are AND-ed together — the variant matches only
 * when every group passes.
 */
export type SegmentGroup = {
    matchMode: "any" | "all";
    segments: SegmentRule[];
};

type PersonalizationSegmentEntry = SegmentRule | SegmentGroup;

const isSegmentGroup = (
    entry: PersonalizationSegmentEntry
): entry is SegmentGroup => "matchMode" in entry && "segments" in entry;

/**
 * Normalize the user's value for a given segment type to a string array.
 * All segment types are normalized to string[] for uniform comparison.
 */
const getUserValues = (
    segmentType: UserSegmentTypes,
    personalizationSegmentValues: PersonalizationUserSegmentValues
): string[] => {
    const raw = personalizationSegmentValues[segmentType];
    if (!raw) return [];
    if (Array.isArray(raw)) return raw as string[];
    return [raw as string];
};

/**
 * Evaluate a single SegmentRule against the user's profile values.
 * Applies the rule's operator (defaults to "is" for legacy data with no operator).
 */
const evaluateSegmentRule = (
    rule: SegmentRule,
    personalizationSegmentValues: PersonalizationUserSegmentValues
): boolean => {
    const op = rule.operator ?? "is";
    const userValues = getUserValues(
        rule.segmentType,
        personalizationSegmentValues
    );

    // Helper: does any rule target match any user value, given a comparator?
    const positiveMatch = (comparator: (u: string, v: string) => boolean) =>
        rule.value.some((v) => userValues.some((u) => comparator(u, v)));

    switch (op) {
        case "is":
        // "matches" is an alias for "is" (exact equality on discrete values)
        case "matches":
            return positiveMatch((u, v) => u === v);
        case "is_not":
        // "not_matches" is an alias for "is_not"
        case "not_matches":
            return !positiveMatch((u, v) => u === v);
        case "includes":
            return positiveMatch((u, v) => u.includes(v));
        case "not_includes":
            return !positiveMatch((u, v) => u.includes(v));
        case "matches_regex":
            // Compile each regex once per target value (not inside the inner loop)
            return rule.value.some((v) => {
                const re = new RegExp(v);
                return userValues.some((u) => re.test(u));
            });
        case "not_matches_regex":
            return !rule.value.some((v) => {
                const re = new RegExp(v);
                return userValues.some((u) => re.test(u));
            });
        default:
            return false;
    }
};

/**
 * Evaluate a SegmentGroup against the user's profile values.
 * matchMode "any" = OR across rules, "all" = AND across rules.
 */
const evaluateSegmentGroup = (
    group: SegmentGroup,
    personalizationSegmentValues: PersonalizationUserSegmentValues
): boolean => {
    const check = group.matchMode === "any" ? "some" : "every";
    return group.segments[check]((rule) =>
        evaluateSegmentRule(rule, personalizationSegmentValues)
    );
};

// Helper function to find matching variant index
const findMatchingVariant = (
    variantCandidates: ComponentInstance[],
    personalizationSegmentValues: PersonalizationUserSegmentValues
): number => {
    if (!personalizationSegmentValues) return -1;

    return variantCandidates.findIndex((variantCandidate) => {
        const rawValue = variantCandidate.parameters?.["personalizationSegment"]
            ?.value as PersonalizationSegmentEntry[] | undefined;

        if (!rawValue || rawValue.length === 0) return false;

        const firstEntry = rawValue[0];
        if (!firstEntry) return false;

        // Detect format by checking whether the first item is a SegmentGroup
        if (isSegmentGroup(firstEntry)) {
            const groups = rawValue as SegmentGroup[];
            // ALL groups must pass (AND between groups)
            return groups.every((group) =>
                evaluateSegmentGroup(group, personalizationSegmentValues)
            );
        }

        // Legacy flat format: all rules are AND-ed together ("is" operator implied when absent)
        const segments = rawValue as SegmentRule[];
        return segments.every((rule) =>
            evaluateSegmentRule(rule, personalizationSegmentValues)
        );
    });
};

// Helper function to get final personalization values
const getFinalPersonalizationValues = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    userSegments: Record<string, any> | undefined,
    overrides?: PersonalizationUserSegmentValues
): PersonalizationUserSegmentValues => {
    const finalValues = {
        ...userSegments,
        ...(overrides?.useOverrides ? overrides : {}),
        useOverrides: overrides?.useOverrides || false,
        enrollInExperiment: overrides?.enrollInExperiment || false
    };
    return finalValues;
};

const ExperimentallyPersonalizedSection = ({
    experimentId,
    setVariantStatus,
    overrides,
    setRenderMessage,
    variantComponent
}: ExperimentallyPersonalizedSectionProps) => {
    const experimentStatus = useExperiment({
        experimentName: experimentId,
        parameterName: "enrolled",
        defaultValue: false,
        options: {
            fireExperimentExposure: true,
            typeGuard: (value: unknown): value is boolean =>
                typeof value === "boolean"
        },
        targeting: {
            cohort: () => true
        },
        metadata: {
            description: "Personalization Experiment - configured in Uniform",
            expectedReleaseDate: "unknown - configured in Uniform",
            expectedRuntime: "unknown - configured in Uniform",
            context: {
                urls: []
            }
        }
    });

    const showTreatment =
        experimentStatus.type === "enrolled" && experimentStatus.value;

    useEffect(() => {
        if (!showTreatment && !overrides?.enrollInExperiment) {
            if (setVariantStatus) {
                setVariantStatus(VariantsStatus.NOT_ENROLLED_IN_EXPERIMENT);
            }
        }
    }, [showTreatment, overrides?.enrollInExperiment, setVariantStatus]);

    if (!showTreatment && !overrides?.enrollInExperiment) {
        return renderDefaultSlot(
            VariantsStatus.NOT_ENROLLED_IN_EXPERIMENT,
            setRenderMessage
        );
    }

    return variantComponent;
};

const getRequiredSegmentTypes = (
    variantCandidates: ComponentInstance[]
): UserSegmentTypes[] => {
    const requiredSegments = new Set<UserSegmentTypes>();

    variantCandidates.forEach((variantCandidate) => {
        const rawValue = variantCandidate.parameters?.["personalizationSegment"]
            ?.value as PersonalizationSegmentEntry[] | undefined;

        if (!rawValue || rawValue.length === 0) return;

        const firstEntry = rawValue[0];
        if (!firstEntry) return;

        if (isSegmentGroup(firstEntry)) {
            // New group format: extract segment types from nested segments
            (rawValue as SegmentGroup[]).forEach((group) => {
                group.segments.forEach(({ segmentType }) =>
                    requiredSegments.add(segmentType)
                );
            });
        } else {
            // Legacy flat format
            (rawValue as SegmentRule[]).forEach(({ segmentType }) =>
                requiredSegments.add(segmentType)
            );
        }
    });

    return [...requiredSegments];
};

type StaticPersonalizationHydrationRule = {
    field: keyof PersonalizationUserSegmentValues;
    sourceKeys: string[];
    hasIncomingValue: (value: unknown) => boolean;
    isMissingOverrideValue: (value: unknown) => boolean;
};

const STATIC_PERSONALIZATION_HYDRATION_RULES: StaticPersonalizationHydrationRule[] =
    [
        {
            field: UserSegmentTypes.CLUSTER_TAG,
            sourceKeys: [UserSegmentTypes.CLUSTER_TAG, "clusterTag", "anchor"],
            hasIncomingValue: (value) =>
                typeof value === "string" && value.length > 0,
            isMissingOverrideValue: (value) =>
                value === undefined || value === ""
        }
    ];

const hydrateStaticPersonalizationValues = (
    previousOverrides: PersonalizationUserSegmentValues,
    personalizationValues: Partial<PersonalizationUserSegmentValues>
) => {
    let didChange = false;
    const nextOverrides = { ...previousOverrides };

    STATIC_PERSONALIZATION_HYDRATION_RULES.forEach(
        ({ field, hasIncomingValue, isMissingOverrideValue }) => {
            const incomingValue = personalizationValues[field];
            if (
                hasIncomingValue(incomingValue) &&
                isMissingOverrideValue(nextOverrides[field])
            ) {
                nextOverrides[field] = incomingValue as never;
                didChange = true;
            }
        }
    );

    return didChange ? nextOverrides : previousOverrides;
};

const readStaticPersonalizationValuesFromQuirks = (
    uniformContext: unknown
): Partial<PersonalizationUserSegmentValues> => {
    const contextWithStorage = uniformContext as
        | {
              quirks?: Record<string, unknown>;
              storage?: { data?: { quirks?: Record<string, unknown> } };
          }
        | undefined;

    const directQuirks = contextWithStorage?.quirks ?? {};
    const storageQuirks = contextWithStorage?.storage?.data?.quirks ?? {};
    const quirks = { ...storageQuirks, ...directQuirks };

    return STATIC_PERSONALIZATION_HYDRATION_RULES.reduce<
        Partial<PersonalizationUserSegmentValues>
    >((acc, { field, sourceKeys, hasIncomingValue }) => {
        const candidateValue = sourceKeys
            .map((sourceKey) => quirks[sourceKey])
            .find((value) => hasIncomingValue(value));
        if (hasIncomingValue(candidateValue)) {
            acc[field] = candidateValue as never;
        }
        return acc;
    }, {});
};

// Helper function to validate personalization prerequisites
const validatePersonalizationPrerequisites = (
    shouldPersonalize: boolean,
    isProd: boolean,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    userSegments: any,
    statuses: Record<UserSegmentTypes, SegmentStatus>,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    error: any,
    segmentFetchErrors: UserSegmentTypes[] | undefined,
    requiredSegmentTypes: UserSegmentTypes[],
    overrides?: PersonalizationUserSegmentValues
): { isValid: boolean; status?: VariantsStatus } => {
    // Check if any required segments are still fetching
    const areRequiredSegmentsFetching = requiredSegmentTypes.some(
        (segmentType) =>
            !statuses[segmentType] ||
            statuses[segmentType] === "fetching" ||
            statuses[segmentType] === "idle"
    );

    if (areRequiredSegmentsFetching) {
        return {
            isValid: false,
            status: VariantsStatus.FETCHING_SEGMENTS
        };
    }
    if (!shouldPersonalize && isProd) {
        return {
            isValid: false,
            status: VariantsStatus.PERSONALIZATION_DISABLED
        };
    }

    if (!userSegments) {
        return {
            isValid: false,
            status: VariantsStatus.MISSING_USER_SEGMENTS
        };
    }

    const hasRequiredSegmentFetchErrors =
        requiredSegmentTypes.length > 0 &&
        (segmentFetchErrors || []).some((segmentType) =>
            requiredSegmentTypes.includes(segmentType)
        );

    if (error && !overrides?.useOverrides && hasRequiredSegmentFetchErrors) {
        return {
            isValid: false,
            status: VariantsStatus.SEGMENT_FETCH_ERROR
        };
    }

    return { isValid: true };
};

// Helper function to create variant component
const createVariantComponent = (chosenVariantIndex: number) => (
    <UniformSlot
        name="variants"
        wrapperComponent={(slotProps) =>
            VariantSelector({
                ...slotProps,
                items: [slotProps.items[chosenVariantIndex]]
            })
        }
    />
);

// Helper function to set render message for variant
const setVariantRenderMessage = (
    variantCandidates: ComponentInstance[],
    chosenVariantIndex: number,
    overrides: PersonalizationUserSegmentValues | undefined,
    setRenderMessage: React.Dispatch<React.SetStateAction<string>>
) => {
    const variantName = getCurrentVariantName(
        variantCandidates,
        chosenVariantIndex
    );
    const sourceType = overrides?.useOverrides ? "Overrides" : "User Segments";

    setRenderMessage(
        `Rendering "${variantName}" Variant based on ${sourceType}`
    );
};

const getCurrentVariantName = (
    variantCandidates: ComponentInstance[],
    chosenVariantIndex: number
) => {
    return (
        variantCandidates[chosenVariantIndex]?.parameters?.["variantName"]
            ?.value || "unknown"
    );
};

const PersonalizedSection = (props: PersonalizedSectionProps) => {
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const analyticsClient = useAnalyticsClient();
    const [variantStatus, setVariantStatus] = useState<VariantsStatus | null>(
        null
    );
    const {
        data: userSegments,
        error,
        segmentFetchErrors,
        statuses
    } = useUserSegments();
    const shouldPersonalize = checkFeatureFlagGate(
        featureFlags.uniformPersonalizationEnabled
    );
    const isProd = isProduction();

    const composition = useUniformCurrentComposition();
    const compositionId = composition?.data?._id;

    // Memoize slots to prevent infinite loops
    const variantSlots = useMemo(
        () => props.component.slots?.["variants"] ?? [],
        [props.component.slots]
    );

    // Compute variant selection synchronously during render (SSR + Client)
    const { selectedVariantKey, computedStatus, renderedExperience } =
        useMemo(() => {
            const validation = validatePersonalizationPrerequisites(
                shouldPersonalize,
                isProd,
                userSegments,
                statuses,
                error,
                segmentFetchErrors,
                props.requiredSegmentTypes,
                props.overrides
            );

            if (!validation.isValid) {
                return {
                    selectedVariantKey: "default_experience",
                    computedStatus: validation.status as VariantsStatus,
                    renderedExperience: (
                        <UniformSlot
                            name="default"
                            wrapperComponent={VariantSelector}
                        />
                    )
                };
            }

            // Process personalization logic
            const personalizationValues = getFinalPersonalizationValues(
                userSegments,
                props.overrides
            );
            const chosenVariantIndex = findMatchingVariant(
                variantSlots,
                personalizationValues
            );

            // Check if variant was found
            if (chosenVariantIndex < 0) {
                return {
                    selectedVariantKey: "default_experience",
                    computedStatus: VariantsStatus.NO_VARIANT_MATCH,
                    renderedExperience: (
                        <UniformSlot
                            name="default"
                            wrapperComponent={VariantSelector}
                        />
                    )
                };
            }

            const variantName = getCurrentVariantName(
                variantSlots,
                chosenVariantIndex
            ) as string;

            const variantComponent = createVariantComponent(chosenVariantIndex);

            // Handle experimental personalization
            if (props.experimentId) {
                return {
                    selectedVariantKey: variantName,
                    computedStatus: VariantsStatus.VARIANTS_AVAILABLE,
                    renderedExperience: (
                        <ExperimentallyPersonalizedSection
                            containerName={props.containerName}
                            setRenderMessage={props.setRenderMessage}
                            setVariantStatus={setVariantStatus}
                            experimentId={props.experimentId}
                            variantComponent={variantComponent}
                            overrides={props.overrides}
                            component={props.component}
                        />
                    )
                };
            }

            return {
                selectedVariantKey: variantName,
                computedStatus: VariantsStatus.VARIANTS_AVAILABLE,
                renderedExperience: variantComponent
            };
        }, [
            error,
            segmentFetchErrors,
            props.overrides,
            props.setRenderMessage,
            props.experimentId,
            props.containerName,
            props.component,
            props.requiredSegmentTypes,
            shouldPersonalize,
            isProd,
            statuses,
            userSegments,
            variantSlots
        ]);

    // Update render message as a side effect (client-only)
    useEffect(() => {
        if (computedStatus === VariantsStatus.VARIANTS_AVAILABLE) {
            const chosenVariantIndex = variantSlots.findIndex(
                (v) =>
                    getCurrentVariantName(
                        variantSlots,
                        variantSlots.indexOf(v)
                    ) === selectedVariantKey
            );
            if (chosenVariantIndex >= 0) {
                setVariantRenderMessage(
                    variantSlots,
                    chosenVariantIndex,
                    props.overrides,
                    props.setRenderMessage
                );
            }
        } else {
            props.setRenderMessage(renderMessages[computedStatus]);
        }
        setVariantStatus(computedStatus);
    }, [
        computedStatus,
        selectedVariantKey,
        variantSlots,
        props.overrides,
        props.setRenderMessage
    ]);

    // Send analytics metrics (client-only)
    useEffect(() => {
        if (!selectedVariantKey || !variantStatus) {
            return;
        }
        if (analyticsClient && typeof window !== "undefined") {
            createAnalyticsEvent({
                name: "waePersonalizationVariantRender",
                action: "personalizationVariantRender",
                actionSubject: "uniformPage",
                eventType: "screen",
                source: window.location.pathname,
                attributes: {
                    containerName: props.containerName,
                    path: window.location.pathname,
                    compositionId: compositionId,
                    variantName: selectedVariantKey,
                    status: variantStatus
                }
            }).fire();
        }
    }, [
        compositionId,
        createAnalyticsEvent,
        selectedVariantKey,
        props.containerName,
        variantStatus,
        analyticsClient
    ]);
    return renderedExperience;
};

const UniformPersonalizationContainer = (
    props: UniformPersonalizationContainerProps
) => {
    const isProd = isProduction();
    const isUniformEditMode =
        typeof window !== "undefined" &&
        window.location.pathname.includes("/uniform-preview");
    const { personalizationValues, uniformContext } = useWAEContext();
    const requiredSegmentTypes = useMemo(
        () =>
            getRequiredSegmentTypes(props.component.slots?.["variants"] ?? []),
        [props.component.slots]
    );
    const [overrides, setOverrides] =
        useState<PersonalizationUserSegmentValues>(() => ({
            useOverrides: false,
            enrollInExperiment: false,
            [UserSegmentTypes.CLUSTER_TAG]:
                personalizationValues?.[UserSegmentTypes.CLUSTER_TAG]
        }));
    const [renderMessage, setRenderMessage] = useState<string>("");
    const [isContainerHovered, setIsContainerHovered] =
        useState<boolean>(false);
    const [isControlsPinned, setIsControlsPinned] = useState<boolean>(false);
    const [isControlsActive, setIsControlsActive] = useState<boolean>(false);
    const isActiveLayer =
        isContainerHovered ||
        isControlsPinned ||
        isControlsActive ||
        isUniformEditMode;

    useEffect(() => {
        if (!personalizationValues) {
            return;
        }

        // `personalizationValues` are seeded in page-wrapper-streaming and mirrored to Uniform quirks.
        // Treat these as static bootstrap values and only fill missing override fields.
        setOverrides((previousOverrides) =>
            hydrateStaticPersonalizationValues(
                previousOverrides,
                personalizationValues as Partial<PersonalizationUserSegmentValues>
            )
        );
    }, [personalizationValues]);

    useEffect(() => {
        if (!isUniformEditMode) {
            return;
        }

        const syncQuirkValues = () => {
            const nextQuirkValues =
                readStaticPersonalizationValuesFromQuirks(uniformContext);
            setOverrides((previousOverrides) =>
                hydrateStaticPersonalizationValues(
                    previousOverrides,
                    nextQuirkValues
                )
            );
        };

        syncQuirkValues();
        const intervalId = window.setInterval(syncQuirkValues, 250);
        return () => {
            window.clearInterval(intervalId);
        };
    }, [isUniformEditMode, uniformContext]);

    return (
        <div
            css={[
                !isProd &&
                    css({
                        // Blue border, dog-ear controls, and hover effects only show in pre-production environments
                        "position": "relative",
                        "outline": `2px solid transparent`,
                        "borderRadius": wacToken("corner.radius.card"),
                        "transition": "outline 0.2s ease",
                        ":hover": {
                            outline: `2px solid ${wacToken(
                                "color.extended.blue300"
                            )}`
                        }
                    }),
                css({
                    zIndex: isActiveLayer ? 2147483000 : 1
                })
            ]}
            onMouseEnter={
                !isProd ? () => setIsContainerHovered(true) : undefined
            }
            onMouseLeave={
                !isProd ? () => setIsContainerHovered(false) : undefined
            }
        >
            {/* Personalization controls only render in pre-production environments */}
            {!isProd && (
                <PersonalizationOverrideControls
                    values={overrides}
                    setValues={setOverrides}
                    renderMessage={renderMessage}
                    isContainerHovered={isContainerHovered}
                    requiredSegmentTypes={requiredSegmentTypes}
                    isPinned={isControlsPinned}
                    setIsPinned={setIsControlsPinned}
                    setIsActive={setIsControlsActive}
                />
            )}
            <PersonalizedSection
                {...props}
                overrides={overrides}
                setRenderMessage={setRenderMessage}
                experimentId={props.experimentId}
                requiredSegmentTypes={requiredSegmentTypes}
            />
        </div>
    );
};

export default UniformPersonalizationContainer;
